import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { careWorkforceService, careWorkforceApiUtils } from '../config/careWorkforceApi';

const ManageCareWorkforceApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchEmail, setSearchEmail] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // Access password - change this to whatever you want
  const ACCESS_PASSWORD = 'PASSword123$';

  // Selection helpers
  const getTopNLatestIds = (n) => {
    if (!applications?.length) return [];
    const sorted = [...applications].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return sorted.slice(0, n).map((a) => a.id);
  };

  const TOP_N = 100;
  const topNIds = useMemo(() => getTopNLatestIds(TOP_N), [applications]);
  const allIds = useMemo(() => applications.map((a) => a.id), [applications]);

  const allSelected = useMemo(
    () => applications.length > 0 && selectedApplications.length === applications.length,
    [applications.length, selectedApplications.length]
  );

  const noneSelected = useMemo(
    () => selectedApplications.length === 0,
    [selectedApplications.length]
  );

  const topNSelected = useMemo(() => {
    if (topNIds.length === 0) return false;
    const selectedSet = new Set(selectedApplications);
    return topNIds.every((id) => selectedSet.has(id));
  }, [topNIds, selectedApplications]);

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      !noneSelected && !allSelected && applications.length > 0;
  }, [noneSelected, allSelected, applications.length]);

  // Auth handlers
  const handleAuth = (e) => {
    e.preventDefault();
    if (password === ACCESS_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('nota_care_workforce_auth', 'true');
    } else {
      setAuthError('Invalid password. Please try again.');
      setPassword('');
    }
  };

  useEffect(() => {
    const isAuth = sessionStorage.getItem('nota_care_workforce_auth');
    if (isAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('nota_care_workforce_auth');
    setPassword('');
  };

  // Data fetch
  const fetchApplications = async (page = 1, email = '', start = '', end = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await careWorkforceService.getApplications({
        page,
        limit: 500,
        ...(email && { email }),
        ...(start && { startDate: start }),
        ...(end && { endDate: end })
      });

      setApplications(response.applications);
      setCurrentPage(response.pagination.currentPage);
      setTotalPages(response.pagination.totalPages);
      setTotalCount(response.pagination.totalCount);
      setSelectedApplications([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchApplications();
    }
  }, [isAuthenticated]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchApplications(1, searchEmail, startDate, endDate);
  };

  const handlePageChange = (page) => {
    fetchApplications(page, searchEmail, startDate, endDate);
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedApplications(allIds);
    } else {
      setSelectedApplications([]);
    }
  };

  const handleSelectTopN = () => {
    if (topNSelected) {
      const topSet = new Set(topNIds);
      setSelectedApplications((prev) => prev.filter((id) => !topSet.has(id)));
    } else {
      setSelectedApplications(topNIds);
    }
  };

  const handleClearSelection = () => setSelectedApplications([]);

  const handleSelectApplication = (id, checked) => {
    if (checked) {
      setSelectedApplications((prev) => [...prev, id]);
    } else {
      setSelectedApplications((prev) => prev.filter((appId) => appId !== id));
    }
  };

  // Delete handlers
  const handleDelete = async (applicationIds) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${applicationIds.length} application(s)? This action cannot be undone.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      await careWorkforceService.deleteApplications(applicationIds);
      fetchApplications(currentPage, searchEmail, startDate, endDate);
      setSelectedApplications([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // CSV export - matches table columns exactly
  const handleExportCSV = () => {
    if (applications.length === 0) return;

    const exportData = applications.map((app) => ({
      'Name': app.fullName,
      'Email': app.email,
      'Nationality': app.nationality || '-',
      'Residence': app.countryOfResidence,
      'Passport': app.hasValidPassport || 'N/A',
      'Police Cert': app.canObtainPoliceClearance || 'N/A',
      'Visited Australia': app.previousVisaApplication || 'N/A',
      'English Test': app.takenEnglishTest === 'Yes' 
        ? `Yes${app.englishTestType ? ` (${app.englishTestType})` : ''}` 
        : 'No',
      'Highest Education': app.highestEducation,
      'Source': app.companySource || 'Direct',
      'Created At': formatDate(app.createdAt)
    }));

    const filename = `care-workforce-applications-${new Date().toISOString().split('T')[0]}.csv`;
    careWorkforceApiUtils.exportToCSV(exportData, filename);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Auth gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <motion.div
          className="max-w-md w-full bg-white rounded-lg shadow-md p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Access Required</h2>
            <p className="text-gray-600">
              Please enter the password to access the care workforce application management system.
            </p>
          </div>

          <form onSubmit={handleAuth}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                placeholder="Enter access password"
                required
              />
            </div>

            {authError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
                {authError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#202356] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors focus:ring-2 focus:ring-[#202356] focus:ring-offset-2"
            >
              Access Management System
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Main UI
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="py-12 px-6 md:px-12 bg-[#202356]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-4xl font-semibold text-white mb-4">
              Manage Care Workforce Applications
            </h1>
            <p className="text-lg text-white opacity-90">
              View, search, and manage all Australia Care Workforce applications
            </p>
            <button onClick={handleLogout} className="mt-4 text-white hover:text-gray-200 transition-colors">
              Logout
            </button>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Filters and Actions */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-6 mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search by Email</label>
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="w-full bg-[#202356] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:bg-gray-400"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportCSV}
                disabled={applications.length === 0}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                Export CSV
              </button>

              {selectedApplications.length > 0 && (
                <button
                  onClick={() => handleDelete(selectedApplications)}
                  disabled={isDeleting}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:bg-gray-400"
                >
                  {isDeleting ? 'Deleting...' : `Delete Selected (${selectedApplications.length})`}
                </button>
              )}
            </div>
          </motion.div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Applications Table */}
          <motion.div
            className="bg-white rounded-lg shadow-md overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Applications ({totalCount} total)
                </h3>

                {applications.length > 0 && (
                  <div className="flex items-center gap-3">
                    <label className="flex items-center">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="mr-2 h-4 w-4 text-[#202356] focus:ring-[#202356] border-gray-300 rounded"
                      />
                      Select All
                    </label>

                    <button
                      type="button"
                      onClick={handleSelectTopN}
                      className={`px-3 py-1 rounded-md text-sm border transition-colors ${
                        topNSelected
                          ? 'bg-[#202356] text-white border-[#202356]'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
                      }`}
                      title={`Select the latest ${Math.min(TOP_N, applications.length)} by Created At`}
                    >
                      {topNSelected
                        ? `Unselect Top ${Math.min(TOP_N, applications.length)}`
                        : `Select Top ${Math.min(TOP_N, applications.length)}`}
                    </button>

                    {!noneSelected && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="px-3 py-1 rounded-md text-sm border border-gray-300 hover:bg-gray-100"
                        title="Clear current selection"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
              {applications.length > 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  "Top {Math.min(TOP_N, applications.length)}" = most recent by <code>createdAt</code> on this page.
                </p>
              )}
            </div>

            {loading ? (
              <div className="px-6 py-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202356] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading applications...</p>
              </div>
            ) : applications.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">No applications found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Select
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nationality
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Residence
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Passport
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Police Cert
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Visited AU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        English Test
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Education
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {applications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedApplications.includes(app.id)}
                            onChange={(e) => handleSelectApplication(app.id, e.target.checked)}
                            className="h-4 w-4 text-[#202356] focus:ring-[#202356] border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{app.fullName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{app.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{app.nationality || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{app.countryOfResidence}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {app.companySource || 'Direct'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            app.hasValidPassport === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {app.hasValidPassport || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            app.canObtainPoliceClearance === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {app.canObtainPoliceClearance || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            app.previousVisaApplication === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {app.previousVisaApplication || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {app.takenEnglishTest === 'Yes' ? (
                              <span className="text-green-600 font-medium">
                                Yes {app.englishTestType ? `(${app.englishTestType})` : ''}
                              </span>
                            ) : (
                              <span className="text-gray-500">No</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{app.highestEducation}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDate(app.createdAt)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleDelete([app.id])}
                            disabled={isDeleting}
                            className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, currentPage - 2) + i;
                      if (pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={`px-3 py-1 border rounded-md text-sm ${
                            pageNum === currentPage
                              ? 'bg-[#202356] text-white border-[#202356]'
                              : 'border-gray-300 hover:bg-gray-100 disabled:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ManageCareWorkforceApplicationsPage;
